import { pathToRegexp } from 'path-to-regexp'
import { getProduct } from './api/product'
import { getActiveOrganization, isAuthorized } from './roles'
import { getWorkOrder } from './api/workorder'
import { matchUrl, navigateToUrl, redirectToSelfService, validUUIDRegExp } from './helpers'
import type { Detail } from './types'

const guardPath = {
  dmsVin: pathToRegexp('/VIN/:vin'),
  dmsWorkOrder: pathToRegexp('/serviceAdvisor/workorders/:workOrderId'),
  product: pathToRegexp('/serviceAdvisor/products/:productId'),
  workOrder: pathToRegexp('/serviceAdvisor/products/:productId/workorders/:workOrderId'),
  workOrderReadonly: pathToRegexp('/serviceAdvisor/products/:productId/workorders/:workOrderId/readonly')
}

export const redirectUserToSelfServiceRule = async () => {
  if (!isAuthorized()) {
    redirectToSelfService()
  }
  return Promise.resolve(false)
}

export const redirectVinToProductRule = async (detail: Detail) => {
  const { newUrl } = detail
  const matchResult = matchUrl(newUrl, guardPath.dmsVin.regexp)

  if (matchResult) {
    const [, vin] = matchResult
    const product = await getProduct(vin)

    if (product) {
      navigateToUrl(`/serviceAdvisor/products/${product[0]?.id}`)
    }

    return true
  }

  return false
}

export const redirectWorkorderToProductRule = async (detail: Detail) => {
  const { newUrl } = detail
  const matchResult = matchUrl(newUrl, guardPath.dmsWorkOrder.regexp)

  if (matchResult) {
    const [, workOrderId] = matchResult

    if (!validUUIDRegExp.test(workOrderId)) {
      navigateToUrl(`/serviceAdvisor/products`)
    } else {
      const workOrder = await getWorkOrder(workOrderId)
      if (workOrder && workOrder.organizationId === getActiveOrganization()) {
        navigateToUrl(`/serviceAdvisor/products/${workOrder.productId}/workorders/${workOrder.id}`)
      } else if (!workOrder) {
        navigateToUrl(`/serviceAdvisor/products`)
      } else {
        navigateToUrl(`/serviceAdvisor/products/${workOrder.productId}`)
      }
    }

    return true
  }

  return false
}

export const redirectProductToProductsOnLoadRule = (detail: Detail) => {
  const { oldUrl, newUrl } = detail

  // only on initial load of app
  if (oldUrl === newUrl) {
    const matchResult = matchUrl(newUrl, guardPath.product.regexp)

    if (matchResult) {
      const [, productId] = matchResult
      if (!validUUIDRegExp.test(productId)) {
        navigateToUrl(`/serviceAdvisor/products`)
      }

      return Promise.resolve(true)
    }
  }

  return Promise.resolve(false)
}

export const guardWorkOrderPathOnLoadRule = async (detail: Detail) => {
  const { oldUrl, newUrl } = detail

  // only on initial load of app
  if (oldUrl === newUrl) {
    const matchResult = matchUrl(newUrl, guardPath.workOrder.regexp)
    const matchResultReadOnly = matchUrl(newUrl, guardPath.workOrderReadonly.regexp)
    const result = matchResult || matchResultReadOnly

    if (result) {
      const [, productId, workOrderId] = result

      if (!validUUIDRegExp.test(productId)) {
        navigateToUrl(`/serviceAdvisor/products`)
        return true
      }

      if (!validUUIDRegExp.test(workOrderId)) {
        navigateToUrl(`/serviceAdvisor/products/${productId}`)
        return true
      }

      const workOrder = await getWorkOrder(workOrderId)

      if (workOrder?.productId !== productId) {
        navigateToUrl(`/serviceAdvisor/products/${productId}`)
        return true
      }

      if (!matchResultReadOnly && workOrder.invoiceTime) {
        navigateToUrl(`/serviceAdvisor/products/${productId}/workorders/${workOrderId}/readonly`)
        return true
      }

      if (workOrder.organizationId !== getActiveOrganization()) {
        navigateToUrl(`/serviceAdvisor/products/${productId}`)
      }
    }
  }

  return false
}

const routeRules: ((detail: Detail) => Promise<boolean>)[] = [
  redirectUserToSelfServiceRule,
  redirectVinToProductRule,
  redirectProductToProductsOnLoadRule,
  redirectWorkorderToProductRule,
  guardWorkOrderPathOnLoadRule
]

export default routeRules
